/**
 * MarketSummaryBar - Top bar showing market status, portfolio value, and today's gain.
 */

import { cn } from '@/lib/utils';
import { getMarketStatus } from '@/utils/marketHours';
import type { PortfolioSummary } from '@/types/portfolio';

interface MarketSummaryBarProps {
  summary: PortfolioSummary | null;
  positionCount: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

export function MarketSummaryBar({ summary, positionCount }: MarketSummaryBarProps) {
  const marketStatus = getMarketStatus();
  const isTodayPositive = summary ? summary.today_gain >= 0 : true;

  const statusConfig = {
    open: { label: 'Market Open', color: 'bg-green-500/10 text-green-400', dot: 'bg-green-400' },
    'pre-market': { label: 'Pre-Market', color: 'bg-amber-500/10 text-amber-400', dot: 'bg-amber-400' },
    'after-hours': { label: 'After Hours', color: 'bg-blue-500/10 text-blue-400', dot: 'bg-blue-400' },
    closed: { label: 'Market Closed', color: 'bg-surface-light text-text-secondary', dot: 'bg-text-muted' },
  };

  const status = statusConfig[marketStatus];

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Market Status */}
      <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium', status.color)}>
        <span className={cn('w-1.5 h-1.5 rounded-full animate-pulse', status.dot)} />
        {status.label}
      </div>

      {/* Portfolio Value */}
      {summary && (
        <>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary tabular-nums">
              {formatCurrency(summary.total_value)}
            </span>
            <span className={cn(
              'text-xs font-medium px-1.5 py-0.5 rounded',
              isTodayPositive ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'
            )}>
              {isTodayPositive ? '+' : ''}{formatCurrency(summary.today_gain)} today
            </span>
          </div>

          <div className="h-4 w-px bg-border" />
          <span className="text-xs text-text-muted">
            {positionCount} position{positionCount !== 1 ? 's' : ''}
          </span>
        </>
      )}
    </div>
  );
}
